import * as THREE from "three";

/** Layer index reserved for interactive entities (enemies, loot, switches). */
export const INTERACTIVE_LAYER = 1;

/**
 * Converts raw DOM mouse coordinates to Normalized Device Coordinates.
 */
export function toNDC(
  mouseX: number,
  mouseY: number,
  width: number,
  height: number,
): THREE.Vector2 {
  return new THREE.Vector2(
    (mouseX / width) * 2 - 1,
    -(mouseY / height) * 2 + 1,
  );
}

/** Data stored in `THREE.Object3D.userData` for pickable entities. */
export interface PickableUserData {
  instanceId: string;
  /** "enemy" | "npc" | "door" | "container" | "interact" — drives click handling in main.tsx. */
  kind?: string;
  /** For NPC sprites: the Ink knot to start when clicked. */
  inkKnot?: string;
  /** For NPC sprites: portrait key or path shown in DialogueBox header. */
  portrait?: string;
  /** For cell-anchored hits (e.g. doors): the grid cell the object sits on. */
  cellX?: number;
  cellZ?: number;
}

/**
 * Cast a ray onto the y=0 ground plane and snap the hit to integer grid
 * coordinates. Used by the S23 spell target picker to translate a click
 * (or tap) into the AoE anchor cell. Returns null when the ray points
 * upward and never intersects the plane.
 */
export function pickGroundCell(
  ndc: THREE.Vector2,
  camera: THREE.Camera,
): { x: number; z: number } | null {
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(ndc, camera);
  const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const point = new THREE.Vector3();
  if (!raycaster.ray.intersectPlane(plane, point)) return null;
  return { x: Math.round(point.x), z: Math.round(point.z) };
}

/**
 * Casts a ray from the camera through the given NDC point and returns
 * the first interactive entity hit (Layer 1 only), or null.
 */
export function pickInteractive(
  ndc: THREE.Vector2,
  camera: THREE.Camera,
  scene: THREE.Scene,
): PickableUserData | null {
  const raycaster = new THREE.Raycaster();
  raycaster.layers.set(INTERACTIVE_LAYER);
  raycaster.setFromCamera(ndc, camera);

  const hits = raycaster.intersectObjects(scene.children, true);
  if (hits.length === 0) return null;

  const first = hits[0]!;
  const ud = first.object.userData as Partial<PickableUserData>;
  if (!ud.instanceId) return null;

  const result: PickableUserData = { instanceId: ud.instanceId };
  if (ud.kind !== undefined) result.kind = ud.kind;
  if (ud.inkKnot !== undefined) result.inkKnot = ud.inkKnot;
  if (ud.portrait !== undefined) result.portrait = ud.portrait;
  if (ud.cellX !== undefined) result.cellX = ud.cellX;
  if (ud.cellZ !== undefined) result.cellZ = ud.cellZ;
  return result;
}
